import { Button, Checkbox, IconButton, Stack, Typography, CircularProgress } from '@mui/material'
import React, { useState, useEffect } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { alertNotification } from 'src/ui'
import * as S from '../styles'
import { ChainAsset, ChainName, StagingChainAsset } from 'src/common/static'
import { getReturnAddress } from 'src/common/api'
import axios from 'axios'

interface BurnInstructionsProps {
  onProceed: () => void
  selectedChain: ChainName
  asset: ChainAsset
}

const BurnInstructions: React.FC<BurnInstructionsProps> = ({
  onProceed,
  selectedChain,
  asset
}: BurnInstructionsProps) => {
  const [checked, setChecked] = useState(false)
  const [returnAddress, setReturnAddress] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const fetchReturnAddress = async () => {
      setLoading(true)
      setError('')
      try {
        // Determine the correct asset based on environment
        let assetToFetch: ChainAsset | StagingChainAsset
        if (
          process.env.REACT_APP_API_URL?.includes('staging') ||
          process.env.REACT_APP_API_URL?.includes('localhost')
        ) {
          assetToFetch = StagingChainAsset.QCAD_TEST
        } else {
          assetToFetch = asset
        }

        const response = await getReturnAddress(selectedChain, assetToFetch)
        setReturnAddress(response.data.address)
      } catch (err) {
        if (axios.isAxiosError(err)) {
          setError('Failed to fetch return address. Please try again.')
          alertNotification('Failed to fetch return address. Please try again.', 'error')
        } else {
          setError('An unexpected error occurred.')
          alertNotification('An unexpected error occurred.', 'error')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchReturnAddress()
  }, [selectedChain, asset])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked)
  }

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
    alertNotification('Successfully copied to clipboard!', 'success')
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: '100%', width: '100%' }}>
      <S.Row>
        <Typography variant="h5" fontWeight={600}>
          Burn Instructions
        </Typography>
        <Typography variant="subtitle1" color="grey.700">
          Please complete the transfer to the {selectedChain} QCAD return address:
        </Typography>
      </S.Row>
      <S.Row sx={{ backgroundColor: 'grey.100', p: 2, borderRadius: 1 }}>
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <CircularProgress size={24} />
            <Typography variant="body2" color="grey.600">
              Loading return address...
            </Typography>
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1" onClick={() => copyToClipboard(returnAddress)}>
              <strong>{returnAddress}</strong>
            </Typography>
            <IconButton onClick={() => copyToClipboard(returnAddress)}>
              <ContentCopyIcon />
            </IconButton>
          </Stack>
        )}
      </S.Row>
      <S.Row>
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox checked={checked} onChange={handleChange} />
          <Typography variant="body2">I have completed the above transaction.</Typography>
        </Stack>
      </S.Row>

      <S.Row>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onProceed}
          disabled={!checked || loading || !!error}
        >
          Proceed
        </Button>
      </S.Row>
    </Stack>
  )
}

export default BurnInstructions
